const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000/api';

class UserService {
  constructor() {
    this.baseURL = `${API_BASE_URL}/users`;
  }

  // Get auth token from localStorage
  getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    };
  }

  // Get all users
  async getAllUsers(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.search) queryParams.append('search', params.search);
      if (params.status) queryParams.append('status', params.status);
      if (params.userType) queryParams.append('userType', params.userType);
      if (params.department) queryParams.append('department', params.department);

      const url = `${this.baseURL}?${queryParams.toString()}`;
      
      const response = await fetch(url, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch users');
      }

      return data;
    } catch (error) {
      console.error('Error fetching users:', error);
      throw error;
    }
  }

  // Get user by ID
  async getUserById(id) {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch user');
      }

      return data;
    } catch (error) {
      console.error('Error fetching user:', error);
      throw error;
    }
  }

  // Create user
  async createUser(userData) {
    try {
      const response = await fetch(this.baseURL, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to create user');
      }

      return data;
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  }

  // Update user
  async updateUser(id, userData) {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        method: 'PUT',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(userData)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to update user');
      }

      return data;
    } catch (error) {
      console.error('Error updating user:', error);
      throw error;
    }
  }

  // Delete user (soft delete)
  async deleteUser(id) {
    try {
      const response = await fetch(`${this.baseURL}/${id}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete user');
      }

      return data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Activate user
  async activateUser(id) {
    try {
      const response = await fetch(`${this.baseURL}/${id}/activate`, {
        method: 'PATCH',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to activate user');
      }

      return data;
    } catch (error) {
      console.error('Error activating user:', error);
      throw error;
    }
  }

  // Get user statistics
  async getUserStats() {
    try {
      const response = await fetch(`${this.baseURL}/stats`, {
        method: 'GET',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch user statistics');
      }

      return data;
    } catch (error) {
      console.error('Error fetching user statistics:', error);
      throw error;
    }
  }

  // Delete user
  async deleteUser(userId) {
    try {
      const response = await fetch(`${this.baseURL}/${userId}`, {
        method: 'DELETE',
        headers: this.getAuthHeaders()
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete user');
      }

      return data;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // Transform user data for display (optimized)
  transformUserForDisplay(user) {
    // Early return for invalid user
    if (!user) return null;

    // Handle role data properly - role is an array
    let roleData = null
    let roleName = 'No Role'
    let roleId = ''
    
    if (user.role && Array.isArray(user.role) && user.role.length > 0) {
      roleData = user.role[0]
      roleName = user.role[0]?.roleName || 'No Role'
      roleId = user.role[0]?._id || user.role[0]?.id || ''
    }

    // Handle department data
    let departmentName = user.departmentName || ''
    if (user.department && typeof user.department === 'object') {
      departmentName = user.department.name || user.departmentName || ''
    }

    // Optimize date formatting
    const formatDate = (date) => {
      if (!date) return '';
      try {
        return new Date(date).toISOString().split('T')[0];
      } catch {
        return '';
      }
    };

    const formatDateDDMMYYYY = (date) => {
      if (!date) return '';
      try {
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0');
        const year = d.getFullYear();
        return `${day}-${month}-${year}`;
      } catch {
        return '';
      }
    };

    return {
      id: user._id || user.id,
      userCode: user.userCode || '',
      name: user.name || '',
      userEmail: user.userEmail || '',
      email: user.email || '',
      phone: user.phone || '',
      role: roleData,
      roleId: roleId,
      roleName: roleName,
      department: user.department,
      departmentName: departmentName,
      designation: user.designation || '',
      status: user.status || '',
      isActive: user.isActive || false,
      userType: user.userType || '',
      lastLogin: user.lastLoggedIn ? formatDateDDMMYYYY(user.lastLoggedIn) : 'Never',
      createdDate: formatDate(user.createdAt),
      lastModified: formatDate(user.updatedAt)
    };
  }

  // Batch transform users for better performance
  transformUsersForDisplay(users) {
    if (!Array.isArray(users)) return [];
    
    return users.map(user => this.transformUserForDisplay(user)).filter(Boolean);
  }

  // Transform user data for submission
  transformUserForSubmission(userData) {
    const roleId = userData.role && String(userData.role).trim();
    const deptVal = userData.department != null ? String(userData.department).trim() : '';
    const payload = {
      userCode: userData.userCode,
      name: userData.name,
      userEmail: userData.email || userData.userEmail,
      email: userData.email || userData.userEmail,
      phone: userData.phone,
      role: roleId ? [roleId] : [],
      designation: userData.designation,
      status: userData.status,
      userType: userData.userType || 'Internal',
      password: userData.password,
      confirmPassword: userData.confirmPassword
    };
    if (deptVal) {
      payload.department = deptVal;
      payload.departmentName = deptVal;
    } else {
      payload.departmentName = '';
      payload.department = null;
    }
    return payload;
  }
}

export default new UserService();
